import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

// The server checks the same three limits; these keep a reader from waiting on an upload it refuses.
export const LEO_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
export const MAX_LEO_IMAGES = 5;
export const MAX_LEO_IMAGE_BYTES = 5 * 1024 * 1024;

export type TStagedLeoImage = { id: string; file: File; previewUrl: string };

// Images waiting in the composer for the reader's next message. Nothing is uploaded until the
// message is sent, so an image removed here never leaves the browser.
export function useLeoImages() {
  const { t } = useTranslation("projectHome");
  const [staged, setStaged] = useState<TStagedLeoImage[]>([]);
  const stagedRef = useRef(staged);
  stagedRef.current = staged;
  const nextId = useRef(0);

  useEffect(() => () => stagedRef.current.forEach((image) => URL.revokeObjectURL(image.previewUrl)), []);

  const add = useCallback(
    (files: File[]) => {
      const images = files.filter((file) => LEO_IMAGE_TYPES.includes(file.type));
      if (images.length < files.length) toast.error(t("leo.images.unsupported"));
      const fitting = images.filter((file) => file.size <= MAX_LEO_IMAGE_BYTES);
      if (fitting.length < images.length) toast.error(t("leo.images.tooLarge"));
      const room = Math.max(0, MAX_LEO_IMAGES - stagedRef.current.length);
      if (fitting.length > room) toast.error(t("leo.images.tooMany", { count: MAX_LEO_IMAGES }));
      const added = fitting.slice(0, room).map((file) => ({
        id: String(nextId.current++),
        file,
        previewUrl: URL.createObjectURL(file)
      }));
      if (added.length > 0) setStaged((current) => [...current, ...added]);
    },
    [t]
  );

  const remove = useCallback((id: string) => {
    setStaged((current) => {
      current.filter((image) => image.id === id).forEach((image) => URL.revokeObjectURL(image.previewUrl));
      return current.filter((image) => image.id !== id);
    });
  }, []);

  // Hands the staged images to the message being sent, which keeps showing their previews.
  const take = useCallback(() => {
    const taken = stagedRef.current;
    stagedRef.current = [];
    setStaged([]);
    return taken;
  }, []);

  return { staged, add, remove, take, isFull: staged.length >= MAX_LEO_IMAGES };
}
